import { useMemo, useState } from "react";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { useTranslation } from "react-i18next";
import type { StaffProfileEmploymentHistory } from "services/api/company/staff.service";
import {
  formatDateLabelInStockholm,
  parseIsoDateToLocalDateOrNull,
} from "web-apps/company/utils/timeAndDate";
import {
  isFutureEmploymentStartDate,
  sortHistoryItemsByStartDateDesc,
} from "../profilePanel.history.logic";
import {
  FieldCard,
  FieldLabel,
  FieldValue,
  Section,
  SectionTitle,
} from "../../layout/profilePanel.styled";
import {
  ActiveBadge,
  BadgeGroup,
  FutureBadge,
  HistoryBody,
  HistoryBodyInner,
  HistoryContainer,
  HistoryDetailsGrid,
  HistoryItem,
  HistoryList,
  HistorySummaryRow,
  SalaryDate,
  SalaryList,
  SalaryRow,
  SalarySection,
  SalaryTitle,
  SalaryValue,
  SummaryAction,
  SummaryCell,
  SummaryDeleteButton,
  SummaryGrid,
  SummaryLabel,
  SummaryTopRow,
  SummaryValue,
} from "./profilePanel.historySection.styled";
import { ProfilePanelDeleteFutureEmploymentModal } from "./profilePanel.deleteFutureEmploymentModal.component";

interface ProfilePanelHistorySectionProps {
  employments: StaffProfileEmploymentHistory[];
  isDeletingEmployment: boolean;
  onDeleteFutureEmployment: (employmentId: number) => Promise<boolean>;
}

const OPEN_ENDED_END_DATE = "9999-12-31";

/**
 * Handles profile panel history section.
 */
export const ProfilePanelHistorySection = (
  props: ProfilePanelHistorySectionProps,
): JSX.Element => {
  const { t } = useTranslation();
  const toDateRangeLabel = (
    startDate: string | null | undefined,
    endDate: string | null | undefined,
  ): string => {
    const displayEndDate =
      endDate != null && endDate.includes(OPEN_ENDED_END_DATE)
        ? t("Staff.ProfilePanelUntilFurtherNotice")
        : (parseIsoDateToLocalDateOrNull(endDate)?.toString() ?? "-");

    return `${parseIsoDateToLocalDateOrNull(startDate)?.toString() ?? "-"} - ${displayEndDate}`;
  };
  const [expandedEmploymentId, setExpandedEmploymentId] = useState<
    number | null
  >(null);
  const [confirmDeleteEmploymentId, setConfirmDeleteEmploymentId] = useState<
    number | null
  >(null);

  const sortedEmployments = useMemo(() => {
    return sortHistoryItemsByStartDateDesc(props.employments).map(
      (employment) => {
        return {
          ...employment,
          salaries: sortHistoryItemsByStartDateDesc(employment.salaries),
        };
      },
    );
  }, [props.employments]);

  return (
    <Section>
      <SectionTitle>{t("General.History")}</SectionTitle>
      <HistoryContainer>
        {sortedEmployments.length === 0 ? (
          <FieldValue>{t("Staff.ProfilePanelNoEmploymentHistory")}</FieldValue>
        ) : (
          <HistoryList>
            {sortedEmployments.map((employment) => {
              const isExpanded = expandedEmploymentId === employment.id;
              const isFuture = isFutureEmploymentStartDate(
                employment.startDate,
              );

              return (
                <HistoryItem key={employment.id}>
                  <HistorySummaryRow
                    type="button"
                    onClick={() =>
                      setExpandedEmploymentId((currentValue) =>
                        currentValue === employment.id ? null : employment.id,
                      )
                    }
                  >
                    <SummaryGrid>
                      <SummaryCell>
                        <SummaryTopRow>
                          <SummaryValue>
                            {toDateRangeLabel(
                              employment.startDate,
                              employment.endDate,
                            )}
                          </SummaryValue>
                        </SummaryTopRow>
                        <SummaryLabel>
                          {t("Staff.ProfilePanelHistoryPeriod")}
                        </SummaryLabel>
                      </SummaryCell>
                      <BadgeGroup>
                        {employment.isActive && (
                          <ActiveBadge>
                            {t("Staff.ProfilePanelActiveTag")}
                          </ActiveBadge>
                        )}
                        {isFuture && (
                          <FutureBadge>
                            {t("Staff.ProfilePanelFutureTag")}
                          </FutureBadge>
                        )}
                      </BadgeGroup>
                    </SummaryGrid>
                    {isFuture && (
                      <SummaryDeleteButton
                        type="button"
                        disabled={props.isDeletingEmployment}
                        aria-label={t(
                          "Staff.ProfilePanelDeleteFutureEmployment",
                        )}
                        onClick={(event) => {
                          event.stopPropagation();
                          setConfirmDeleteEmploymentId(employment.id);
                        }}
                      >
                        <CustomIcon
                          name="v2/trash"
                          size="20px"
                          color={ColorV2.NegativeRed}
                        />
                      </SummaryDeleteButton>
                    )}
                    <SummaryAction>
                      <CustomIcon
                        name={isExpanded ? "v2/up-chevron" : "v2/down-chevron"}
                        size="20px"
                        color={ColorV2.Blue4}
                      />
                    </SummaryAction>
                  </HistorySummaryRow>

                  <HistoryBody $expanded={isExpanded}>
                    <HistoryBodyInner>
                      <HistoryDetailsGrid>
                        <FieldCard>
                          <FieldValue>
                            {employment.agreement?.trim() || "-"}
                          </FieldValue>
                          <FieldLabel>
                            {t(
                              "Staff.AddStaffPanelFieldCollectiveSalaryAgreement",
                            )}
                          </FieldLabel>
                        </FieldCard>
                        <FieldCard>
                          <FieldValue>
                            {employment.employeeCategory === "Undefined" ||
                            !employment.employeeCategory
                              ? "-"
                              : (() => {
                                  const translationKey = `Staff.employeeCategory${employment.employeeCategory}`;
                                  const translatedValue = t(translationKey);
                                  if (translatedValue !== translationKey) {
                                    return translatedValue;
                                  }
                                  return employment.employeeCategory;
                                })()}
                          </FieldValue>
                          <FieldLabel>
                            {t("Staff.AddStaffPanelFieldStaffType")}
                          </FieldLabel>
                        </FieldCard>
                        <FieldCard>
                          <FieldValue>
                            {String(employment.employeeNumber)}
                          </FieldValue>
                          <FieldLabel>
                            {t("Staff.AddStaffPanelFieldEmployeeNumber")}
                          </FieldLabel>
                        </FieldCard>
                        <FieldCard>
                          <FieldValue>
                            {employment.employmentType
                              ? t(
                                  `Staff.employmentType${employment.employmentType}`,
                                )
                              : "-"}
                          </FieldValue>
                          <FieldLabel>{t("Staff.EmploymentType")}</FieldLabel>
                        </FieldCard>
                        <FieldCard>
                          <FieldValue>
                            {String(employment.noticeDays)}
                          </FieldValue>
                          <FieldLabel>
                            {t("Staff.AddStaffPanelFieldNoticePeriodDays")}
                          </FieldLabel>
                        </FieldCard>
                        <FieldCard>
                          <FieldValue>
                            {employment.paymentType
                              ? employment.paymentType === "None"
                                ? t("System.OwnSalaryManagement")
                                : "SimflexityPay"
                              : "-"}
                          </FieldValue>
                          <FieldLabel>
                            {t("Staff.AddStaffPanelFieldPaymentSolution")}
                          </FieldLabel>
                        </FieldCard>
                        <FieldCard>
                          <FieldValue>
                            {formatDateLabelInStockholm(
                              employment.probationPeriodEndDate ?? "",
                              "sv-SE",
                            )}
                          </FieldValue>
                          <FieldLabel>
                            {t("Staff.ProfilePanelFieldProbationPeriodEndDate")}
                          </FieldLabel>
                        </FieldCard>
                        <FieldCard>
                          <FieldValue>{employment.role}</FieldValue>
                          <FieldLabel>
                            {t("Staff.AddStaffPanelFieldRole")}
                          </FieldLabel>
                        </FieldCard>
                        <FieldCard>
                          <FieldValue>
                            {employment.salaryType
                              ? t(`Staff.salaryType${employment.salaryType}`)
                              : "-"}
                          </FieldValue>
                          <FieldLabel>
                            {t("Staff.AddStaffPanelFieldSalaryModel")}
                          </FieldLabel>
                        </FieldCard>
                        <FieldCard>
                          <FieldValue>
                            {String(employment.vacationDays)}
                          </FieldValue>
                          <FieldLabel>
                            {t("Staff.AddStaffPanelFieldVacationDays")}
                          </FieldLabel>
                        </FieldCard>
                        <FieldCard>
                          <FieldValue>
                            {employment.workPercentage == null
                              ? "-"
                              : `${Math.round(employment.workPercentage * 100)}%`}
                          </FieldValue>
                          <FieldLabel>
                            {`${t("Staff.AddStaffPanelFieldWorkingHours")} (%)`}
                          </FieldLabel>
                        </FieldCard>
                        <FieldCard>
                          <FieldValue>
                            {employment.workplace?.trim() || "-"}
                          </FieldValue>
                          <FieldLabel>
                            {t("Staff.AddStaffPanelFieldWorkPlace")}
                          </FieldLabel>
                        </FieldCard>
                      </HistoryDetailsGrid>

                      <SalarySection>
                        <SalaryTitle>
                          {t("Staff.ProfilePanelSalaries")}
                        </SalaryTitle>
                        {employment.salaries.length === 0 ? (
                          <FieldValue>-</FieldValue>
                        ) : (
                          <SalaryList>
                            {employment.salaries.map((salary) => (
                              <SalaryRow key={salary.id}>
                                <SalaryDate>
                                  {parseIsoDateToLocalDateOrNull(
                                    salary.startDate,
                                  )?.toString() ?? "-"}
                                </SalaryDate>
                                <SalaryValue>
                                  {salary.value == null
                                    ? "-"
                                    : (() => {
                                        const displayValue = String(
                                          salary.value,
                                        ).trim();
                                        return displayValue.length > 0
                                          ? `${displayValue} kr`
                                          : "-";
                                      })()}
                                </SalaryValue>
                              </SalaryRow>
                            ))}
                          </SalaryList>
                        )}
                      </SalarySection>
                    </HistoryBodyInner>
                  </HistoryBody>
                </HistoryItem>
              );
            })}
          </HistoryList>
        )}
      </HistoryContainer>
      <ProfilePanelDeleteFutureEmploymentModal
        isOpen={confirmDeleteEmploymentId != null}
        isDeletingEmployment={props.isDeletingEmployment}
        onClose={() => {
          if (props.isDeletingEmployment) return;
          setConfirmDeleteEmploymentId(null);
        }}
        onConfirm={() => {
          if (confirmDeleteEmploymentId == null) return;
          props
            .onDeleteFutureEmployment(confirmDeleteEmploymentId)
            .then((ok) => {
              if (ok) {
                setConfirmDeleteEmploymentId(null);
              }
            });
        }}
      />
    </Section>
  );
};
